import {
  CloseOutlined,
  CopyOutlined,
  DownOutlined,
  EditOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  LockOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Input as AntdInput, Tooltip } from "antd";
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type HTMLAttributes,
  type Ref,
} from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import {
  getBetCurrencyOptions,
  getCurrencyRewardTypeOptions,
  getRewardTypeOptions,
} from "../../../../queries/game-pass/gamePassOptions";
import { useGetGamePassConfig } from "../../../../queries/game-pass/useGetGamePassConfig";
import { Button, Input, Select } from "../../../shared";
import {
  DEFAULT_MONTHLY_ACTION,
  DEFAULT_MONTHLY_CASINO_ACTION,
  DESCRIPTION_MAX,
  MONTHLY_TASK_TYPE_OPTIONS,
  UTC_STREAK_RESET_HOURS,
  createEmptyTier,
  isMinLegsBelowFloor,
  isMonthlyChallengeValid,
  MIN_LEGS_PARAM,
  usesSportsbookPickFields,
  type LabeledOption,
  type MonthlyChallengeDraft,
  type MonthlyTaskType,
  type MonthlyTierDraft,
} from "../monthlyChallengeData";
import {
  buildDefaultRewardParams,
  isParamValueComplete,
} from "../../CreateSeason/createSeasonData";
import MonthlyCasinoPicksFields from "./MonthlyCasinoPicksFields";
import MonthlySportsbookPicksFields from "./MonthlySportsbookPicksFields";
import MonthlyTierRow from "./MonthlyTierRow";

import styles from "./MonthlyChallengeCard.module.css";

const { TextArea } = AntdInput;

/**
 * Drag-and-drop bindings supplied by the parent list that owns the
 * `<SortableContext>` (see `MonthlyChallengesStep`). When present the card
 * becomes a sortable item: `setNodeRef` + `transformStyle` drive the moving
 * `<section>`, `attributes` are spread on it for a11y, and `listeners` +
 * `setActivatorNodeRef` are wired onto the drag-handle button so a drag can
 * only start from the handle. Omit it to keep the handle inert (disabled).
 */
export type MonthlyChallengeCardDragProps = {
  setNodeRef: Ref<HTMLElement>;
  setActivatorNodeRef: Ref<HTMLButtonElement>;
  attributes: HTMLAttributes<HTMLElement>;
  listeners: HTMLAttributes<HTMLElement> | undefined;
  transformStyle: CSSProperties;
  isDragging: boolean;
};

export type MonthlyChallengeCardProps = {
  challenge: MonthlyChallengeDraft;
  index: number;
  onChange: (next: MonthlyChallengeDraft) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  /** Commit the challenge: mark it saved and collapse to the summary view. */
  onSave: () => void;
  /** Re-open a collapsed challenge for editing. */
  onEdit: () => void;
  /** Close the expanded editor: discard an unsaved draft, or collapse a
   *  previously-saved challenge back to its summary. */
  onClose: () => void;
  /** Disable Duplicate — set while another unsaved draft is already open, so a
   *  copy (which opens as a new unsaved draft) can't break the one-draft-at-a
   *  -time invariant. */
  disableDuplicate?: boolean;
  /** Visual mode. `"edit"` (default) is the wizard / Scheduled in-place editor.
   *  `"locked"` is the read-only Active view (FE-2276): the header shows a
   *  "Locked" pill instead of Duplicate / Drag / Edit, name + description stay
   *  read-only, the Steps bar expands to inspect the (disabled) tiers, and there
   *  is no Add Step / Save / Delete. `onChange` / `onSave` / etc. are never
   *  invoked in this mode, so locked call sites can pass no-ops. */
  mode?: "edit" | "locked";
  drag?: MonthlyChallengeCardDragProps;
};

/**
 * A single monthly challenge, rendered in one of two modes driven by
 * `challenge.isExpanded`:
 *
 * - **Collapsed (saved) summary** — name + description (read-only) and a
 *   "Task Details" bar; the header offers Duplicate / Drag / Edit.
 * - **Expanded editor** — name, description, task type and the tier list edited
 *   in place; the header offers Duplicate / Drag / Close, and a footer holds
 *   "Save Challenge" (plus Delete once the challenge has been saved before).
 *
 * Unlike a season challenge (tasks live in a modal), a monthly challenge is
 * fully edited in place — the task type drives which fields the tier list
 * collects.
 */
const MonthlyChallengeCard: FC<MonthlyChallengeCardProps> = ({
  challenge,
  index,
  onChange,
  onDuplicate,
  onDelete,
  onSave,
  onEdit,
  onClose,
  disableDuplicate = false,
  mode = "edit",
  drag,
}) => {
  const idPrefix = `monthly-challenge-card-${index}`;
  const isLocked = mode === "locked";

  // Locked cards start collapsed but the Steps bar can toggle the read-only
  // tier list open/closed (mirrors Seasons' locked ChallengeCard, which reveals
  // its task list inline). Seeded from the record's `isExpanded` so a mock /
  // API row flagged as expanded opens showing its tiers.
  const [areTiersVisible, setAreTiersVisible] = useState<boolean>(
    Boolean(challenge.isExpanded),
  );

  const taskTypeOptions = useMemo<
    ReadonlyArray<LabeledOption<MonthlyTaskType>>
  >(
    () =>
      MONTHLY_TASK_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: AppStrings[option.labelKey],
      })),
    [],
  );

  // Reward + Wager Currency options both come from `GET /config`. Once the
  // catalogue is in, the tier reward dropdown matches Seasons
  // (`getRewardTypeOptions`): currency rewards plus available bonus types
  // (Deposit Match, Free Pick, Profit Boost). While config is loading / empty,
  // fall back to the two platform currencies only — never XP or the static
  // extras. Wager Currency stays on the bet-currency catalogue.
  const { data: gamePassConfig } = useGetGamePassConfig();
  const rewardOptions = useMemo(() => {
    const hasApiRewards =
      (gamePassConfig?.reward_types?.length ?? 0) > 0 ||
      (gamePassConfig?.bonus_reward_types?.length ?? 0) > 0;
    return hasApiRewards
      ? getRewardTypeOptions(gamePassConfig)
      : getCurrencyRewardTypeOptions(gamePassConfig);
  }, [gamePassConfig]);
  const rewardTypeOptions = useMemo<ReadonlyArray<LabeledOption<string>>>(
    () =>
      rewardOptions.map((option) => ({
        value:
          option.kind === "bonus"
            ? (option.bonusCurrency ?? option.value)
            : option.value,
        label: option.label,
      })),
    [rewardOptions],
  );
  const wagerCurrencyOptions = useMemo(
    () => getBetCurrencyOptions(gamePassConfig),
    [gamePassConfig],
  );

  // "Sportsbook Picks" and "Rebets" (same UI per FE-2441) reveal the Pick Type
  // / Action / Sports / Leagues / Minimum Odds / Minimum Bet Amount / Wager
  // Currency fields; "Casino Picks" reveals the Provider / Game Title / Action
  // / Wager Currency fields. Every other task type keeps the task-details
  // section unchanged.
  const showsSportsbookFields = usesSportsbookPickFields(challenge.taskType);
  const isCasinoPicks = challenge.taskType === "CASINO_PICKS";
  const isLoginStreak = challenge.taskType === "LOGIN_STREAK";

  // The Casino Picks picker lifts two patches per game toggle (games + labels)
  // in a single synchronous event. The parent rebuilds the challenge from a
  // stale render closure, so a second `onChange` built off the original prop
  // would clobber the first — reverting a deselect, or dropping a new game's
  // label. Accumulate synchronous patches onto a ref so each patch composes on
  // the previous one and the parent's last-write-wins carries the merged
  // result. The ref is re-synced to the prop on every render.
  const latestChallengeRef = useRef(challenge);
  latestChallengeRef.current = challenge;

  const applyChallengePatch = useCallback(
    (patch: Partial<MonthlyChallengeDraft>) => {
      const next = { ...latestChallengeRef.current, ...patch };
      latestChallengeRef.current = next;
      onChange(next);
    },
    [onChange],
  );

  const findRewardOption = (rewardType?: string) =>
    rewardOptions.find(
      (candidate) =>
        candidate.value === rewardType ||
        candidate.bonusCurrency === rewardType ||
        candidate.rewardType === rewardType,
    );

  const handleTierChange = (tierIndex: number, next: MonthlyTierDraft) => {
    const previous = challenge.tiers[tierIndex];
    const option = findRewardOption(next.rewardType);
    const rewardTypeChanged = previous?.rewardType !== next.rewardType;
    const stamped: MonthlyTierDraft =
      option?.kind === "bonus"
        ? {
            ...next,
            bonusRewardType: option.bonusRewardType,
            bonusCurrency: option.bonusCurrency,
            params: rewardTypeChanged
              ? buildDefaultRewardParams(option.params)
              : next.params,
          }
        : {
            ...next,
            bonusRewardType: undefined,
            bonusCurrency: undefined,
            params: rewardTypeChanged ? undefined : next.params,
          };
    onChange({
      ...challenge,
      tiers: challenge.tiers.map((tier, i) =>
        i === tierIndex ? stamped : tier,
      ),
    });
  };

  const handleAddTier = () => {
    onChange({ ...challenge, tiers: [...challenge.tiers, createEmptyTier()] });
  };

  const handleRemoveTier = (tierIndex: number) => {
    onChange({
      ...challenge,
      tiers: challenge.tiers.filter((_, i) => i !== tierIndex),
    });
  };

  // The Task Type field is identical across task types; only the select's width
  // differs (fixed for the standalone row, full-width inside the casino grid).
  // `disabled` renders it read-only for the locked (Active) view.
  const renderTaskTypeField = (selectClassName: string, disabled = false) => (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>
        {AppStrings.MONTHLY_CHALLENGE_TASK_TYPE_LABEL}
        <Tooltip title={AppStrings.MONTHLY_CHALLENGE_TASK_TYPE_INFO}>
          <InfoCircleOutlined
            className={styles.infoIcon}
            aria-label={AppStrings.MONTHLY_CHALLENGE_TASK_TYPE_INFO}
          />
        </Tooltip>
      </span>
      <Select<MonthlyTaskType>
        className={selectClassName}
        value={challenge.taskType}
        options={[...taskTypeOptions]}
        aria-label={AppStrings.MONTHLY_CHALLENGE_TASK_TYPE_LABEL}
        disabled={disabled}
        onChange={(value) =>
          onChange({
            ...challenge,
            taskType: value,
          })
        }
        data-testid={`${idPrefix}-task-type`}
      />
    </label>
  );

  const resetHourOptions = useMemo(
    () =>
      UTC_STREAK_RESET_HOURS.map((hour) => ({
        value: String(hour),
        label: formatString(AppStrings.GAME_PASS_MONTHLY_STREAK_RESET_HOUR_OPTION, {
          hour: String(hour).padStart(2, "0"),
        }),
      })),
    [],
  );

  const renderResetHourField = (disabled = false) => (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>
        {AppStrings.GAME_PASS_MONTHLY_STREAK_RESET_HOUR_LABEL}
        <Tooltip title={AppStrings.GAME_PASS_MONTHLY_STREAK_RESET_HOUR_INFO}>
          <InfoCircleOutlined
            className={styles.infoIcon}
            aria-label={AppStrings.GAME_PASS_MONTHLY_STREAK_RESET_HOUR_INFO}
          />
        </Tooltip>
      </span>
      <Select<string>
        className={styles.resetHourSelect}
        value={challenge.streakResetHour || undefined}
        options={resetHourOptions}
        placeholder={AppStrings.GAME_PASS_MONTHLY_STREAK_RESET_HOUR_PLACEHOLDER}
        aria-label={AppStrings.GAME_PASS_MONTHLY_STREAK_RESET_HOUR_LABEL}
        disabled={disabled}
        virtual={false}
        onChange={(value) =>
          applyChallengePatch({
            streakResetHour: value,
          })
        }
        data-testid={`${idPrefix}-streak-reset-hour`}
      />
    </label>
  );

  // Task-details body shared by the expanded editor and the locked (read-only)
  // view: Task Type + the task-type-specific fields, then the Tier list. The
  // `disabled` flag flows into every control so the locked view is fully
  // inert; the editor's "Add Step" button lives outside this helper (locked
  // cards don't render it).
  const renderTaskDetailsInner = (disabled: boolean) => (
    <>
      <span className={styles.sectionLabel}>
        {AppStrings.MONTHLY_CHALLENGE_TASK_DETAILS}
      </span>

      {isCasinoPicks ? (
        // Casino layout: Task Type | Action / Provider and Games (full width) /
        // Wager Currency | Minimum Bet Amount. Task Type and the casino cells
        // are direct grid items so they auto-place column-by-column in DOM order
        // (the Provider and Games picker spans both columns).
        <div className={styles.casinoTaskGrid}>
          {renderTaskTypeField(styles.gridSelect, disabled)}
          <MonthlyCasinoPicksFields
            providers={challenge.casinoProviders ?? []}
            games={challenge.casinoGames ?? []}
            gameLabels={challenge.casinoGameLabels ?? {}}
            action={challenge.casinoAction ?? DEFAULT_MONTHLY_CASINO_ACTION}
            minBet={challenge.casinoMinBet ?? ""}
            wagerCurrency={challenge.wagerCurrency}
            wagerCurrencyOptions={wagerCurrencyOptions}
            onChange={applyChallengePatch}
            disabled={disabled}
            idPrefix={idPrefix}
          />
        </div>
      ) : showsSportsbookFields ? (
        // Sportsbook layout: Task Type | Pick Type / Minimum Odds | Action /
        // Sports | Leagues / Minimum Bet Amount | Wager Currency. Task Type and
        // the sportsbook cells are direct grid items so they auto-place
        // column-by-column in DOM order.
        <div className={styles.sportsbookTaskGrid}>
          {renderTaskTypeField(styles.gridSelect, disabled)}
          <MonthlySportsbookPicksFields
            pickType={challenge.pickType ?? "ANY"}
            minimumOdds={challenge.minimumOdds ?? ""}
            action={challenge.sportsbookAction ?? DEFAULT_MONTHLY_ACTION}
            sports={challenge.sports ?? []}
            leagues={challenge.leagues ?? []}
            minBet={challenge.sportsbookMinBet ?? ""}
            wagerCurrency={challenge.wagerCurrency}
            wagerCurrencyOptions={wagerCurrencyOptions}
            onChange={applyChallengePatch}
            disabled={disabled}
            idPrefix={idPrefix}
          />
        </div>
      ) : (
        <div className={styles.taskTypeRow}>
          {renderTaskTypeField(styles.taskTypeSelect, disabled)}
          {isLoginStreak ? renderResetHourField(disabled) : null}
        </div>
      )}
      {isLoginStreak && (
        <p
          className={styles.taskTypeHint}
          data-testid={`${idPrefix}-login-streak-hint`}
        >
          {AppStrings.GAME_PASS_MONTHLY_LOGIN_STREAK_INFO}
        </p>
      )}

      <span className={styles.sectionLabel}>
        {AppStrings.MONTHLY_CHALLENGE_TIERS_LABEL}
      </span>

      <div className={styles.tiers}>
        {challenge.tiers.map((tier, tierIndex) => (
          <MonthlyTierRow
            key={tier.id}
            index={tierIndex}
            tier={tier}
            rewardTypeOptions={rewardTypeOptions}
            rewardOption={findRewardOption(tier.rewardType)}
            onChange={(next) => handleTierChange(tierIndex, next)}
            onRemove={() => handleRemoveTier(tierIndex)}
            removable={challenge.tiers.length > 1}
            disabled={disabled}
            idPrefix={idPrefix}
          />
        ))}
      </div>
    </>
  );

  const areBonusParamsComplete = challenge.tiers.every((tier) => {
    const option = findRewardOption(tier.rewardType);
    if (option?.kind !== "bonus") return true;
    return (option.params ?? []).every((param) => {
      if (param.name === MIN_LEGS_PARAM) {
        return !isMinLegsBelowFloor(tier.params?.[param.name]);
      }
      return (
        !param.required ||
        isParamValueComplete(param.type, tier.params?.[param.name])
      );
    });
  });
  const isValid = isMonthlyChallengeValid(challenge) && areBonusParamsComplete;
  const title = `${AppStrings.MONTHLY_CHALLENGE_CARD_TITLE_PREFIX} ${index + 1}`;
  const cardClassName = `${styles.card} ${
    drag?.isDragging ? styles.cardDragging : ""
  }`.trim();

  const duplicateButton = (
    <button
      type="button"
      className={styles.iconButton}
      aria-label={AppStrings.CHALLENGE_DUPLICATE}
      onClick={onDuplicate}
      disabled={disableDuplicate}
      title={
        disableDuplicate
          ? AppStrings.ADD_CHALLENGE_FINISH_DRAFT_FIRST
          : undefined
      }
      data-testid={`${idPrefix}-duplicate`}
    >
      <CopyOutlined />
    </button>
  );

  // Native <button> (not the shared icon button) so dnd-kit's activator ref +
  // listeners can be forwarded; a drag can only be started from here. Stays
  // disabled/inert when `drag` is absent.
  const dragHandleButton = (
    <button
      type="button"
      ref={drag?.setActivatorNodeRef}
      className={`${styles.iconButton} ${drag ? styles.dragHandleActive : ""}`.trim()}
      aria-label={AppStrings.CHALLENGE_DRAG_HANDLE}
      data-testid={`${idPrefix}-drag`}
      disabled={!drag}
      {...(drag?.listeners ?? {})}
    >
      <HolderOutlined />
    </button>
  );

  // Locked (Active) view — fully read-only. No Duplicate / Drag / Edit, no
  // Save / Delete / Add Step; the header carries a "Locked" pill + info icon
  // and the Steps bar toggles the disabled tier list open for inspection.
  if (isLocked) {
    return (
      <section className={cardClassName} data-testid={idPrefix}>
        <header className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
          <span
            className={styles.lockedBadge}
            data-testid={`${idPrefix}-locked-badge`}
          >
            <LockOutlined className={styles.lockedBadgeIcon} aria-hidden="true" />
            {AppStrings.SEASON_CARD_LOCKED}
            <Tooltip title={AppStrings.MONTHLY_CHALLENGE_LOCKED_INFO}>
              <InfoCircleOutlined
                className={styles.infoIcon}
                aria-label={AppStrings.MONTHLY_CHALLENGE_LOCKED_INFO}
              />
            </Tooltip>
          </span>
        </header>

        <div className={styles.summaryFields}>
          <div
            className={styles.summaryName}
            data-testid={`${idPrefix}-summary-name`}
          >
            {challenge.name || title}
          </div>
          <div
            className={`${styles.summaryText} ${
              challenge.description ? "" : styles.summaryPlaceholder
            }`.trim()}
            data-testid={`${idPrefix}-summary-description`}
          >
            {challenge.description ||
              AppStrings.MONTHLY_CHALLENGE_CARD_DESCRIPTION_PLACEHOLDER}
          </div>
        </div>

        <button
          type="button"
          className={styles.summaryBar}
          onClick={() => setAreTiersVisible((visible) => !visible)}
          aria-expanded={areTiersVisible}
          data-testid={`${idPrefix}-summary-bar`}
        >
          <span>{AppStrings.MONTHLY_CHALLENGE_TASK_DETAILS}</span>
          <DownOutlined
            className={`${styles.summaryBarIcon} ${
              areTiersVisible ? styles.summaryBarIconOpen : ""
            }`.trim()}
          />
        </button>

        {areTiersVisible ? (
          <div
            className={styles.taskDetails}
            data-testid={`${idPrefix}-task-details`}
          >
            {renderTaskDetailsInner(true)}
          </div>
        ) : null}
      </section>
    );
  }

  // Collapsed summary — the saved-challenge view. Name + description are shown
  // read-only above a "Task Details" bar; Edit (or the bar itself) re-opens the
  // editor.
  if (!challenge.isExpanded) {
    return (
      <section
        ref={drag?.setNodeRef}
        className={cardClassName}
        style={drag?.transformStyle}
        {...(drag?.attributes ?? {})}
        data-testid={idPrefix}
      >
        <header className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
          <div className={styles.headerActions}>
            {duplicateButton}
            {dragHandleButton}
            <button
              type="button"
              className={styles.iconButton}
              aria-label={AppStrings.MONTHLY_CHALLENGE_EDIT_ARIA}
              onClick={onEdit}
              data-testid={`${idPrefix}-edit`}
            >
              <EditOutlined />
            </button>
          </div>
        </header>

        <div className={styles.summaryFields}>
          <div
            className={styles.summaryName}
            data-testid={`${idPrefix}-summary-name`}
          >
            {challenge.name || title}
          </div>
          <div
            className={`${styles.summaryText} ${
              challenge.description ? "" : styles.summaryPlaceholder
            }`.trim()}
            data-testid={`${idPrefix}-summary-description`}
          >
            {challenge.description ||
              AppStrings.MONTHLY_CHALLENGE_CARD_DESCRIPTION_PLACEHOLDER}
          </div>
        </div>

        <button
          type="button"
          className={styles.summaryBar}
          onClick={onEdit}
          data-testid={`${idPrefix}-summary-bar`}
        >
          <span>{AppStrings.MONTHLY_CHALLENGE_TASK_DETAILS}</span>
          <DownOutlined className={styles.summaryBarIcon} />
        </button>
      </section>
    );
  }

  return (
    <section
      ref={drag?.setNodeRef}
      className={cardClassName}
      style={drag?.transformStyle}
      // dnd-kit attaches role / aria-roledescription / tabIndex via
      // `attributes`, spread on the same element that carries the sortable ref
      // so screen readers announce the card as a sortable item.
      {...(drag?.attributes ?? {})}
      data-testid={idPrefix}
    >
      <header className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.headerActions}>
          {duplicateButton}
          {dragHandleButton}
          {/* Close discards an unsaved draft or collapses a saved challenge
              (the parent decides); Delete lives in the footer once saved. */}
          <button
            type="button"
            className={styles.iconButton}
            aria-label={AppStrings.MONTHLY_CHALLENGE_CLOSE_ARIA}
            onClick={onClose}
            data-testid={`${idPrefix}-close`}
          >
            <CloseOutlined />
          </button>
        </div>
      </header>

      <div className={styles.fields}>
        <Input
          className={styles.nameInput}
          value={challenge.name}
          placeholder={AppStrings.MONTHLY_CHALLENGE_CARD_NAME_PLACEHOLDER}
          onChange={(e) => onChange({ ...challenge, name: e.target.value })}
          data-testid={`${idPrefix}-name`}
        />
        <div className={styles.descriptionWrapper}>
          <TextArea
            className={`custom-form-input ${styles.descriptionInput}`}
            value={challenge.description}
            autoSize={{ minRows: 3, maxRows: 5 }}
            maxLength={DESCRIPTION_MAX}
            placeholder={
              AppStrings.MONTHLY_CHALLENGE_CARD_DESCRIPTION_PLACEHOLDER
            }
            onChange={(e) =>
              onChange({ ...challenge, description: e.target.value })
            }
            data-testid={`${idPrefix}-description`}
          />
          <span
            className={styles.charCount}
            aria-label={AppStrings.CREATE_TASK_DESCRIPTION_COUNTER_ARIA}
            data-testid={`${idPrefix}-description-count`}
          >
            {`${challenge.description.length}/${DESCRIPTION_MAX}`}
          </span>
        </div>
      </div>

      <div className={styles.taskDetails}>
        {renderTaskDetailsInner(false)}

        <button
          type="button"
          className={styles.addStepButton}
          onClick={handleAddTier}
          data-testid={`${idPrefix}-add-step`}
        >
          <PlusOutlined className={styles.addStepIcon} />
          {AppStrings.MONTHLY_CHALLENGE_ADD_STEP}
        </button>
      </div>

      <div className={styles.footer}>
        {challenge.isSaved ? (
          <Button
            intent="danger"
            variant="outline"
            onClick={onDelete}
            data-testid={`${idPrefix}-delete`}
          >
            {AppStrings.CHALLENGE_DELETE}
          </Button>
        ) : null}
        <Button
          intent="primary"
          onClick={onSave}
          disabled={!isValid}
          data-testid={`${idPrefix}-save`}
        >
          {AppStrings.MONTHLY_CHALLENGE_SAVE}
        </Button>
      </div>
    </section>
  );
};

export default MonthlyChallengeCard;
